import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, LinearProgress, Alert,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, List, ListItem, ListItemText,
} from '@mui/material';
import { ChevronLeft, ChevronRight, CalendarToday, Close } from '@mui/icons-material';
import { apiFetch as rawApiFetch } from '../../services/apiFetch';

function apiFetch(path: string) {
  return rawApiFetch(path)
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw d; return d; });
}

type EventType = 'holiday' | 'compliance' | 'payroll' | 'leave' | 'probation';

const TYPE_CFG: Record<EventType, { color: string; bg: string; label: string }> = {
  holiday:    { color: '#e65100', bg: '#fff3e0', label: '🏖 Holiday' },
  compliance: { color: '#880e4f', bg: '#fce4ec', label: '📋 Compliance' },
  payroll:    { color: '#2e7d32', bg: '#e8f5e9', label: '💰 Payroll' },
  leave:      { color: '#4a148c', bg: '#f3e5f5', label: '🌴 Leave' },
  probation:  { color: '#bf360c', bg: '#fbe9e7', label: '👤 Probation' },
};

interface CalEvent { date: string; type: EventType; title: string; detail?: string; id?: string; }

function getDaysInMonth(year: number, month: number) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday=0
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function HRCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dayDlg, setDayDlg] = useState<{ date: string; events: CalEvent[] } | null>(null);
  const [typeFilter, setTypeFilter] = useState<Set<EventType>>(new Set());

  const toggleFilter = (t: EventType) => setTypeFilter(p => {
    const n = new Set(p);
    n.has(t) ? n.delete(t) : n.add(t);
    return n;
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const monthStr = String(month + 1).padStart(2, '0');
    const start = `${year}-${monthStr}-01`;
    const end   = `${year}-${monthStr}-${String(getDaysInMonth(year, month)).padStart(2, '0')}`;

    const collected: CalEvent[] = [];

    const results = await Promise.allSettled([
      // Public holidays
      apiFetch(`/api/public-holidays?year=${year}`).then((r: any) => {
        const list: any[] = Array.isArray(r) ? r : r.data || [];
        list.filter(h => h.date >= start && h.date <= end).forEach(h =>
          collected.push({ date: h.date, type: 'holiday', title: h.name, detail: h.description })
        );
      }),
      // Compliance-doc expiries (real route: /api/compliance-docs)
      apiFetch(`/api/compliance-docs`).then((r: any) => {
        const list: any[] = Array.isArray(r) ? r : r.data || [];
        list.filter(c => c.expiryDate && c.expiryDate >= start && c.expiryDate <= end).forEach(c =>
          collected.push({
            date: c.expiryDate,
            type: 'compliance',
            title: `${c.employee?.firstName ?? ''} ${c.docType ?? c.type ?? 'compliance doc'} expiring`.trim(),
            detail: c.docNumber ?? c.issuingAuthority,
          })
        );
      }),
      // Payroll runs
      apiFetch(`/api/payroll/runs?month=${year}-${monthStr}`).then((r: any) => {
        const list: any[] = Array.isArray(r) ? r : r.data || [];
        list.filter(p => p.payDate >= start && p.payDate <= end).forEach(p =>
          collected.push({ date: p.payDate, type: 'payroll', title: `Payroll: ${p.periodName}`, detail: `Net: HK$${Number(p.totalNetPay || 0).toLocaleString()}` })
        );
      }),
      // Approved leave in this month (real route: /api/leave/requests/calendar)
      apiFetch(`/api/leave/requests/calendar?startDate=${start}&endDate=${end}`).then((r: any) => {
        const list: any[] = Array.isArray(r) ? r : r.data || [];
        list
          .filter(l => (l.status ? l.status === 'approved' : true))
          .slice(0, 30)
          .forEach(l => collected.push({
            date: l.startDate,
            type: 'leave',
            title: `${l.employee?.firstName ?? ''} ${l.employee?.lastName ?? ''} on leave`,
            detail: `${l.leaveType?.name ?? 'Leave'} · ${l.startDate} – ${l.endDate}`,
          }));
      }),
      // Probation reviews due (real route: /api/performance/probation; endDate = review-due)
      apiFetch(`/api/performance/probation`).then((r: any) => {
        const list: any[] = Array.isArray(r) ? r : r.data || [];
        list
          .map(p => ({ ...p, reviewDate: (p.extendedEndDate ?? p.endDate)?.slice?.(0, 10) }))
          .filter(p => p.reviewDate && p.reviewDate >= start && p.reviewDate <= end)
          .forEach(p =>
            collected.push({
              date: p.reviewDate,
              type: 'probation',
              title: `${p.employee?.firstName ?? ''} probation review`,
              detail: p.status,
            })
          );
      }),
    ]);

    setEvents(collected);
    setLoading(false);
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const getEventsForDay = (day: number): CalEvent[] => {
    const d = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === d && (typeFilter.size === 0 || typeFilter.has(e.type)));
  };

  const cells = Array.from({ length: firstDay + daysInMonth }, (_, i) => i < firstDay ? null : i - firstDay + 1);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarToday sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>HR Master Calendar</Typography>
            <Typography variant="body2" color="text.secondary">Holidays, events, compliance deadlines, payroll, leave & more</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={prevMonth}><ChevronLeft /></IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ minWidth: 180, textAlign: 'center' }}>
            {MONTH_NAMES[month]} {year}
          </Typography>
          <IconButton onClick={nextMonth}><ChevronRight /></IconButton>
          <Button size="small" variant="outlined" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }}>Today</Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>Some data sources unavailable — showing partial results.</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Legend / filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {(Object.entries(TYPE_CFG) as [EventType, any][]).map(([t, cfg]) => (
          <Chip key={t} label={cfg.label} size="small"
            sx={{
              bgcolor: typeFilter.size === 0 || typeFilter.has(t) ? cfg.bg : '#f5f5f5',
              color: typeFilter.size === 0 || typeFilter.has(t) ? cfg.color : '#9e9e9e',
              fontWeight: 600, cursor: 'pointer', border: `1px solid ${cfg.color}40`,
              opacity: typeFilter.size > 0 && !typeFilter.has(t) ? 0.5 : 1,
            }}
            onClick={() => toggleFilter(t)}
          />
        ))}
        {typeFilter.size > 0 && (
          <Chip label="Clear" size="small" variant="outlined" onClick={() => setTypeFilter(new Set())} />
        )}
      </Box>

      {/* Calendar grid */}
      <Paper sx={{ overflow: 'hidden' }}>
        {/* Day headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: '#1a237e' }}>
          {DAY_NAMES.map(d => (
            <Box key={d} sx={{ p: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="white" fontWeight={700}>{d}</Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar cells */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #e0e0e0', borderTop: 'none' }}>
          {cells.map((day, idx) => {
            if (!day) return <Box key={`empty-${idx}`} sx={{ bgcolor: '#fafafa', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', minHeight: 90 }} />;
            const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = getEventsForDay(day);
            const isToday = dayStr === todayStr;
            const isWeekend = (idx % 7) >= 5;

            return (
              <Box key={day}
                onClick={() => dayEvents.length > 0 && setDayDlg({ date: dayStr, events: dayEvents })}
                sx={{
                  borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                  minHeight: 90, p: 0.5,
                  bgcolor: isWeekend ? '#fafafa' : 'white',
                  cursor: dayEvents.length > 0 ? 'pointer' : 'default',
                  '&:hover': dayEvents.length > 0 ? { bgcolor: '#f5f5ff' } : {},
                  position: 'relative',
                }}>
                <Box sx={{
                  width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', mb: 0.5,
                  bgcolor: isToday ? '#1a237e' : 'transparent',
                }}>
                  <Typography variant="caption" fontWeight={isToday ? 900 : 400}
                    color={isToday ? 'white' : isWeekend ? 'text.disabled' : 'text.primary'}>
                    {day}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  {dayEvents.slice(0, 3).map((e, i) => (
                    <Box key={i} sx={{ bgcolor: TYPE_CFG[e.type].bg, borderLeft: `3px solid ${TYPE_CFG[e.type].color}`, px: 0.5, borderRadius: '0 2px 2px 0' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: TYPE_CFG[e.type].color, fontWeight: 600, lineHeight: 1.3, display: 'block' }} noWrap>
                        {e.title}
                      </Typography>
                    </Box>
                  ))}
                  {dayEvents.length > 3 && (
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', pl: 0.5 }}>
                      +{dayEvents.length - 3} more
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Day detail dialog */}
      <Dialog open={!!dayDlg} onClose={() => setDayDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dayDlg && new Date(dayDlg.date + 'T00:00:00').toLocaleDateString('en-HK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setDayDlg(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            {dayDlg?.events.map((e, i) => (
              <ListItem key={i} sx={{ bgcolor: TYPE_CFG[e.type].bg, borderRadius: 1, mb: 0.5, borderLeft: `4px solid ${TYPE_CFG[e.type].color}` }}>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={700} color={TYPE_CFG[e.type].color}>{e.title}</Typography>}
                  secondary={e.detail && <Typography variant="caption" color="text.secondary">{e.detail}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setDayDlg(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
